import { errors, Transfer as TransferSdk } from '@smash-sdk/transfer/ejs/01-2024';
import { UploaderStatus } from '../globals/constant';
import { CreateFile } from '../modules/tasks/CreateFile';
import { CreateTransfer } from '../modules/tasks/CreateTransfer';
import { Files } from './Files';
import { Logger } from './Logger';
import { Queue } from './Queue';
import { Transfer } from './Transfer';
import { TransferAlreadyStartingError, TransferAlreadyFinishingError, TransferAlreadyFinishedError, TransferAlreadyCancelingError, TransferAlreadyCanceledError, TransferIsInErrorError, InvalidParameterError, TransferNotFoundError, TransferAlreadyStartedError } from '../errors/errors';
import { UploaderError } from '../SmashUploader';
export class Context {
    constructor(uploaderParameters) {
        this.lastValueUploadedBytes = 0;
        this.speed = 0;
        this.remainingTime = 0;
        this.estimatedTime = 0;
        this.percent = 0;
        this.status = UploaderStatus.Pending;
        this.taskQueue = new Queue();
        this.createTransferQueue = new Queue();
        this.createFileQueue = new Queue();
        this.createPartsQueue = new Queue();
        this.uploadPartQueue = new Queue();
        this.updateFileQueue = new Queue();
        this.updatePartsQueue = new Queue();
        this.lockTransferQueue = new Queue();
        this.partRetriesCountMap = new Map();
        this.uploaderParameters = uploaderParameters;
        this.logger = new Logger(uploaderParameters.verboseLevel);
        this.transferSdk = new TransferSdk({
            region: uploaderParameters.region,
            token: uploaderParameters.token,
            refreshTokenMethod: uploaderParameters.refreshTokenMethod,
        });
    }
    createTransfer(transferParameters) {
        this.transfer = new Transfer(transferParameters);
        this.createTransferQueue.add(new CreateTransfer(this));
        return this;
    }
    checkFilesUpdatable() {
        switch (this.status) {
            case UploaderStatus.Starting:
                throw new TransferAlreadyStartingError('Unable to update transfer files, upload is starting.');
            case UploaderStatus.Started:
                throw new TransferAlreadyStartedError('Unable to update transfer files, upload is started.');
            case UploaderStatus.Paused:
                throw new TransferAlreadyStartedError('Unable to update transfer files, upload is started.');
            case UploaderStatus.Finishing:
                throw new TransferAlreadyFinishingError('Unable to update transfer files, upload is finishing.');
            case UploaderStatus.Finished:
                throw new TransferAlreadyFinishedError('Unable to update transfer files, upload is finished.');
            case UploaderStatus.Canceling:
                throw new TransferAlreadyCancelingError('Unable to update transfer files, upload is canceling.');
            case UploaderStatus.Canceled:
                throw new TransferAlreadyCanceledError('Unable to update transfer files, upload is canceled.');
            case UploaderStatus.Error:
                throw new TransferIsInErrorError('Unable to update transfer files, upload is in error.');
        }
    }
    updateTransfer(params) {
        return new Promise(async (resolve, reject) => {
            var _a, _b, _c;
            try {
                if (this.status === UploaderStatus.Finished) {
                    throw new TransferAlreadyFinishedError('Unable to update transfer, upload is finished.');
                }
                if (params === null || params === void 0 ? void 0 : params.files) {
                    this.checkFilesUpdatable();
                    this.transfer.files = new Files(params.files);
                    this.createFileQueue = new Queue();
                    this.transfer.files.forEach(file => {
                        this.createFileQueue.add(new CreateFile(this, file));
                    });
                }
                const response = await this.transferSdk.updateTransfer({ transferId: this.transfer.id, ...params, size: (_a = this.transfer) === null || _a === void 0 ? void 0 : _a.files.size, filesNumber: (_b = this.transfer) === null || _b === void 0 ? void 0 : _b.files.length });
                this.transfer.populateUpdatedTransfer(response);
                if (((_c = this.transfer) === null || _c === void 0 ? void 0 : _c.queue) && this.transfer.queue > 0) {
                    this.localEndOfQueueTimestamp = Date.now() + this.transfer.queue * 1000;
                }
                else {
                    this.localEndOfQueueTimestamp = undefined;
                }
                resolve({
                    transfer: {
                        id: response.transfer.id,
                        status: response.transfer.status,
                        region: response.transfer.region,
                        transferUrl: response.transfer.transferUrl,
                        uploadState: response.transfer.uploadState,
                        availabilityDuration: response.transfer.availabilityDuration,
                        size: response.transfer.size,
                        preview: response.transfer.preview,
                        created: response.transfer.created,
                        modified: response.transfer.modified,
                        filesNumber: response.transfer.filesNumber,
                        queue: response.transfer.queue,
                        queuedUntil: response.transfer.queuedUntil,
                    }
                });
            }
            catch (error) {
                if (error instanceof errors.UpdateTransfer.TransferAlreadyLockedError) {
                    reject(new InvalidParameterError(error));
                }
                if (error instanceof errors.UpdateTransfer.TransferAlreadyLockedError) {
                    reject(new TransferAlreadyFinishedError(error));
                }
                if (error instanceof errors.UpdateTransfer.InternalServerError
                    || error instanceof errors.UpdateTransfer.BadGatewayError
                    || error instanceof errors.UpdateTransfer.NetworkError
                    || error instanceof errors.UpdateTransfer.GatewayTimeoutError
                    || error instanceof errors.UpdateTransfer.UnknownError) {
                    reject(new UploaderError(error));
                }
                if (error instanceof errors.UpdateTransferError.NotFoundError) {
                    reject(new TransferNotFoundError(error));
                }
                reject(error);
            }
        });
    }
    cancel() {
        this.status = UploaderStatus.Canceling;
        this.cancelDate = new Date().toISOString();
        return this;
    }
    starting() {
        this.status = UploaderStatus.Starting;
        this.startingDate = new Date().toISOString();
        return this;
    }
    started() {
        this.status = UploaderStatus.Started;
        this.startedDate = new Date().toISOString();
        return this;
    }
    queued() {
        this.status = UploaderStatus.Queued;
        this.queuedDate = new Date().toISOString();
        return this;
    }
    paused() {
        this.status = UploaderStatus.Paused;
        this.pausedDate = new Date().toISOString();
        return this;
    }
    resumable() {
        this.status = UploaderStatus.Resumable;
        return this;
    }
    finishing() {
        this.status = UploaderStatus.Finishing;
        this.finishingDate = new Date().toISOString();
        return this;
    }
    finished() {
        this.status = UploaderStatus.Finished;
        this.finishedDate = new Date().toISOString();
        return this;
    }
    error() {
        this.status = UploaderStatus.Error;
        return this;
    }
    isStarted() {
        return this.status === UploaderStatus.Started;
    }
    isFinished() {
        return this.status === UploaderStatus.Finished;
    }
    isCanceled() {
        return this.status === UploaderStatus.Canceling || this.status === UploaderStatus.Canceled;
    }
    isOnError() {
        return this.status === UploaderStatus.Error;
    }
    isPaused() {
        return this.status === UploaderStatus.Paused;
    }
    isQueued() {
        return this.status === UploaderStatus.Queued;
    }
    isResumable() {
        return this.status === UploaderStatus.Resumable;
    }
    hasQueue() {
        var _a;
        return typeof ((_a = this.transfer) === null || _a === void 0 ? void 0 : _a.queue) === "number" && this.transfer.queue > 0;
    }
    unrecoverableUploadError() {
        this.status = UploaderStatus.Error;
        return this;
    }
    onError() {
        return this.status === UploaderStatus.Error;
    }
    shouldStopProcessTask() {
        return this.isPaused() || this.isCanceled() || this.onError();
    }
    getStatus() {
        return this.status;
    }
    queues() {
        return {
            taskQueue: this.taskQueue,
            createTransferQueue: this.createTransferQueue,
            createFileQueue: this.createFileQueue,
            createPartsQueue: this.createPartsQueue,
            uploadPartQueue: this.uploadPartQueue,
            updateFileQueue: this.updateFileQueue,
            updatePartsQueue: this.updatePartsQueue,
            lockTransferQueue: this.lockTransferQueue,
        };
    }
    get uploadedBytes() {
        if (this.transfer) {
            const a = this.transfer.filesCompleted.size;
            const b = this.transfer.filesProcessing.list().reduce((prev, curr) => prev + curr.partsToUpload.sizeUploaded + curr.inlinePartsToComplete.size + curr.partsToComplete.size + curr.completingParts.size + curr.partsCompleted.size, 0);
            return a + b;
        }
        return 0;
    }
    reset() {
        this.transfer = undefined;
        this.status = UploaderStatus.Pending;
        this.taskQueue = new Queue();
        this.createTransferQueue = new Queue();
        this.createFileQueue = new Queue();
        this.createPartsQueue = new Queue();
        this.uploadPartQueue = new Queue();
        this.updateFileQueue = new Queue();
        this.updatePartsQueue = new Queue();
        this.lockTransferQueue = new Queue();
        this.startingDate = undefined;
        this.startedDate = undefined;
        this.uploadingDate = undefined;
        this.finishingDate = undefined;
        this.finishedDate = undefined;
        this.cancelDate = undefined;
        this.lastValueUploadedBytes = 0;
        this.speed = 0;
        this.remainingTime = 0;
        this.estimatedTime = 0;
        this.percent = 0;
    }
}
